import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildSalesDeliveryPrint } from "@/modules/docs/print/doc-print-math";
import { DataTable } from "@/modules/shell/data-table";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { getSalesDelivery } from "@/shared/api/sales";
import { salesDeliveryLineColumns } from "./so-detail-columns";

export function SalesDeliveryDetailPage() {
  const { companySlug, deliveryId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getSalesDelivery>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!companySlug || !deliveryId) return;
    getSalesDelivery(companySlug, deliveryId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, deliveryId, navigate]);

  if (!companySlug || !deliveryId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading delivery…</Alert>
        )}
      </PageFrame>
    );
  }

  const { delivery: del, lines } = data;
  const printData = buildSalesDeliveryPrint({
    number: del.number,
    soNumber: del.soNumber,
    lifecycle: del.lifecycle,
    warehouseName: del.warehouseName,
    warehouseCode: del.warehouseCode,
    issuedAt: del.postedAt,
    lines: lines.map((l) => ({
      productName: l.productName,
      qty: l.qty,
      unitCost: l.unitCost,
    })),
  });

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} />
      </div>
      <div className="no-print">
        <PageHeader
          eyebrow="Delivery"
          title={del.number}
          description={[
            del.lifecycle,
            `← ${del.soNumber}`,
            del.warehouseCode || del.warehouseName,
          ]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => printDocSlip()}
            >
              Print
            </Button>
          }
        />
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="sales_delivery"
          docId={deliveryId}
          linkPath={`/c/${companySlug}/sales/deliveries/${deliveryId}`}
        />
      </div>

      <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-[var(--color-muted)]">Warehouse</dt>
        <dd className="m-0">
          {del.warehouseName}
          {del.warehouseCode ? (
            <span className="font-mono text-xs text-[var(--color-muted)]">
              {" "}
              · {del.warehouseCode}
            </span>
          ) : null}
        </dd>
        {del.postedAt && (
          <>
            <dt className="text-[var(--color-muted)]">Posted</dt>
            <dd className="m-0 text-xs">{del.postedAt}</dd>
          </>
        )}
      </dl>

      <p className="mt-3 text-sm">
        <Link
          to={`/c/${companySlug}/sales/orders/${del.salesOrderId}`}
          className="no-underline"
        >
          Open sales order {del.soNumber} →
        </Link>
      </p>

      <DataTable
        listId="sales-delivery-lines"
        companySlug={companySlug}
        className="mt-6 border-t border-[var(--color-border)]/90 pt-4"
        columns={salesDeliveryLineColumns()}
        data={lines}
        getRowId={(r) => r.id}
        manualSorting={false}
        empty={
          <EmptyState
            title="No lines"
            description="This delivery has no product lines."
          />
        }
      />

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="sales_delivery"
        docId={deliveryId}
        linkPath={`/c/${companySlug}/sales/deliveries/${deliveryId}`}
      />
    </PageFrame>
  );
}
